import React, { useEffect, useState } from 'react';
import { Loader2, Package, MapPin, Phone, Mail, ChevronDown, ChevronUp, CheckCircle2, Truck, PackageCheck, Wrench, FileText } from 'lucide-react';
import { adminApi } from '../lib/api';
import { publicApi } from '../lib/api';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const STATUS_STEPS = [
  { id: 'confirmed', label: 'Confirmée', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-100' },
  { id: 'preparing', label: 'En préparation', icon: Wrench, color: 'text-amber-600 bg-amber-100' },
  { id: 'shipped', label: 'Expédiée', icon: Truck, color: 'text-blue-600 bg-blue-100' },
  { id: 'delivered', label: 'Livrée', icon: PackageCheck, color: 'text-purple-600 bg-purple-100' },
];

const ALL_STATUSES = [
  { id: 'pending', label: 'En attente', color: 'bg-slate-100 text-slate-700' },
  { id: 'confirmed', label: 'Confirmée', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'preparing', label: 'En préparation', color: 'bg-amber-100 text-amber-700' },
  { id: 'shipped', label: 'Expédiée', color: 'bg-blue-100 text-blue-700' },
  { id: 'delivered', label: 'Livrée', color: 'bg-purple-100 text-purple-700' },
  { id: 'cancelled', label: 'Annulée', color: 'bg-red-100 text-red-700' },
];

const OrderRow = ({ order, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState('');
  const [showNoteFor, setShowNoteFor] = useState(null);

  const statusMeta = ALL_STATUSES.find(s => s.id === order.status) || ALL_STATUSES[0];
  const currentStep = STATUS_STEPS.findIndex(s => s.id === order.status);
  const history = order.tracking_history || [];

  const changeStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await onUpdate(order.id, { status: newStatus, tracking_note: note || undefined });
      setNote(''); setShowNoteFor(null);
    } finally { setUpdating(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50/50" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono font-bold text-slate-900">{order.reference}</div>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${statusMeta.color}`}>{statusMeta.label}</span>
            {order.payment?.status === 'paid' && (
              <span className="text-xs text-emerald-600 font-semibold">✓ Payée {order.payment.provider?.toUpperCase()}</span>
            )}
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {order.address?.fullName} · {order.items?.length} article{order.items?.length > 1 ? 's' : ''} · {new Date(order.created_at).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-[#E89534]">{formatFCFA(order.total)} F</div>
          <div className="text-xs text-slate-400">{expanded ? 'Réduire' : 'Détails'}</div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-5 bg-slate-50/30">
          {/* Progress steps */}
          {order.status !== 'cancelled' && (
            <div className="mb-6">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-4 left-4 right-4 h-0.5 bg-slate-200" />
                <div
                  className="absolute top-4 left-4 h-0.5 bg-[#F4A340] transition-all duration-500"
                  style={{ width: currentStep > 0 ? `calc(${(currentStep / (STATUS_STEPS.length - 1)) * 100}% - 32px)` : '0' }}
                />
                {STATUS_STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const isActive = i <= currentStep;
                  return (
                    <div key={s.id} className="relative flex flex-col items-center z-10">
                      <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        isActive ? 'bg-[#F4A340] border-[#F4A340] text-white' : 'bg-white border-slate-300 text-slate-400'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className={`text-[10px] mt-1 font-semibold uppercase tracking-wider ${isActive ? 'text-[#E89534]' : 'text-slate-400'}`}>
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Client & livraison</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /><span>{order.address?.email}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /><span>{order.address?.phone}</span></div>
                <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5" /><span>{order.address?.address}<br /><span className="text-slate-500">{order.address?.city}</span></span></div>
                {order.address?.notes && <div className="text-xs text-slate-500 italic bg-white p-2 rounded-lg mt-1">Note client : {order.address.notes}</div>}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Articles ({order.items?.length})</div>
              <ul className="space-y-1 text-sm">
                {order.items?.map((it, i) => (
                  <li key={i} className="flex justify-between"><span>{it.qty} × {it.name}</span><span className="font-semibold">{formatFCFA(it.qty * it.price)} F</span></li>
                ))}
              </ul>
              <div className="mt-2 pt-2 border-t border-slate-200 text-sm">
                <div className="flex justify-between text-slate-500"><span>Sous-total</span><span>{formatFCFA(order.subtotal)} F</span></div>
                <div className="flex justify-between text-slate-500"><span>Livraison</span><span>{formatFCFA(order.delivery_fee)} F</span></div>
                <div className="flex justify-between font-bold mt-1"><span>Total</span><span className="text-[#E89534]">{formatFCFA(order.total)} F</span></div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Mettre à jour le statut</div>
              <a
                href={publicApi.invoiceUrl(order.reference)}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> Facture PDF
              </a>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {ALL_STATUSES.filter(s => s.id !== 'pending').map((s) => (
                <button
                  key={s.id}
                  onClick={() => setShowNoteFor(showNoteFor === s.id ? null : s.id)}
                  disabled={updating || order.status === s.id}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${
                    order.status === s.id ? 'bg-slate-900 text-white cursor-default' : `${s.color} hover:opacity-80`
                  } disabled:opacity-50`}
                >{s.label}</button>
              ))}
            </div>
            {showNoteFor && (
              <div className="flex gap-2">
                <input
                  type="text" placeholder="Note optionnelle (ex: transporteur, référence colis...)"
                  value={note} onChange={(e) => setNote(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm"
                />
                <button onClick={() => changeStatus(showNoteFor)} disabled={updating} className="px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white rounded-xl text-sm font-semibold">
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                </button>
              </div>
            )}
          </div>

          {/* Timeline */}
          {history.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Historique</div>
              <ul className="space-y-2">
                {history.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#F4A340] mt-1.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div><b>{ALL_STATUSES.find(s => s.id === h.status)?.label || h.status}</b> par {h.by}</div>
                      {h.note && <div className="text-xs text-slate-500 italic">{h.note}</div>}
                      <div className="text-[10px] text-slate-400">{new Date(h.at).toLocaleString('fr-FR')}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OrdersManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.getOrders()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpdate = async (id, body) => {
    await adminApi.updateOrderStatus(id, body);
    load();
  };

  const filtered = filter ? items.filter(o => o.status === filter) : items;

  const stats = {
    total: items.length,
    pending: items.filter(o => o.status === 'confirmed' || o.status === 'preparing').length,
    shipped: items.filter(o => o.status === 'shipped').length,
    delivered: items.filter(o => o.status === 'delivered').length,
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Commandes boutique</h3>
        <p className="text-sm text-slate-500">
          {stats.total} au total · {stats.pending} à traiter · {stats.shipped} en cours de livraison · {stats.delivered} livrées
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('')} className={`px-4 py-2 rounded-full text-sm font-medium ${filter === '' ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'}`}>Toutes</button>
        {ALL_STATUSES.filter(s => s.id !== 'pending').map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} className={`px-4 py-2 rounded-full text-sm font-medium ${filter === s.id ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'}`}>{s.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Aucune commande</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => <OrderRow key={o.id} order={o} onUpdate={handleUpdate} />)}
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
